<template>
  <form class="form-card-container-wrapper" @submit.prevent="submitForm">
    <BaseCard class="form-card-container">
      <!-- Private Profile Section -->
      <h2 class="header-title text-left">Private Profile</h2>
      <div class="private-form-container">
        <div class="private-grid-form">
          <div class="form-group firstname-input">
            <label>First Name</label>
            <input v-model="form.firstName" type="text" placeholder="Enter First Name" />
          </div>
          <div class="form-group lastname-input">
            <label>Last Name*</label>
            <input v-model="form.lastName" type="text" placeholder="Enter Last Name" required />
          </div>
          <div class="form-group email-input">
            <label>Email Address*</label>
            <input v-model="form.email" type="email" placeholder="Enter Email Address" required />
          </div>
          <div class="form-group linkedin-input">
            <label>Linkedin Profile URL</label>
            <input v-model="form.linkedin" type="url" placeholder="Enter Linkedin Profile URL" />
          </div>
          <div class="form-group github-input">
            <label>Github Profile URL</label>
            <input v-model="form.github" type="url" placeholder="Enter Github Profile URL" />
          </div>
          <div class="form-group portfolio-input">
            <label>Website/Portfolio URL</label>
            <input v-model="form.portfolio" type="url" placeholder="Enter Website/Portfolio URL" />
          </div>
        </div>
        <div class="work-permit-container">
          <div class="form-group checkbox-group">
            <label>
              <input type="checkbox" v-model="form.requiresWorkPermit" />
              Requires Work Permit
            </label>
          </div>
          <div class="form-group checkbox-group">
            <label>
              <input type="checkbox" v-model="form.basedInThailand" />
              Based in Thailand or Open to Relocation
            </label>
          </div>
        </div>
        <div class="why-we-ask-container">
          <h5 class="why-we-ask-title">Why we ask about nationality and location?</h5>
          <p class="why-we-ask-message">
            TeamStack is remote-first, but we believe in the power of occasional in-person
            collaboration. Our teams may hold one or more multi-day retreats each year, where
            customers visit and co-work with us here in Thailand.
          </p>
        </div>
        <div class="what-this-means-container">
          <h5 class="what-this-means-title">Why we ask about nationality and location?</h5>
          <p class="what-this-means-message">
            ✔️ TeamStack covers retreat costs (lodging, meals, event expenses).
          </p>
          <p class="what-this-means-message-content">
            <span>❌ We don’t cover travel costs to and from the retreat.</span>
            <span
              >You can live outside Thailand, but you’ll need to cover your own travel expenses to
              attend retreats.</span
            >
          </p>
        </div>
      </div>
    </BaseCard>

    <BaseCard class="form-card-container">
      <!-- Public Profile Section -->
      <h2 class="header-title text-left">Public Profile</h2>
      <div class="public-form-container">
        <div class="form-group">
          <label>Profile Name*</label>
          <input v-model="form.profileName" type="text" placeholder="Enter profile Name" required />
          <p class="helper">Enter a short, friendly name (e.g., Ohm, Maya, Liam).</p>
        </div>

        <div class="form-group">
          <label>Job Domain*</label>
          <select v-model="form.jobDomain" aria-placeholder="Select primary domain" required>
            <option value="" disabled>Select primary domain</option>
            <option value="engineering">Engineering</option>
            <option value="marketing">Marketing</option>
            <option value="design">Design</option>
          </select>
          <p class="helper">Your primary domain of knowledge and work. Add details below.</p>
        </div>

        <div class="form-group">
          <label>Years of Experience*</label>
          <input
            v-model="form.experience"
            type="number"
            min="0"
            placeholder="Select years"
            required
          />
          <p class="helper">Total years of relevant professional experience (e.g., “6” years).</p>
        </div>

        <div class="form-group">
          <label>Sector Experience*</label>
          <input v-model="form.sector" type="text" placeholder="List sectors" required />
          <p class="helper">List sectors you’ve worked in (e.g., Retail, Bangkok, EdTech).</p>
        </div>

        <div class="form-group">
          <label>Key Skills*</label>
          <input v-model="form.skills" type="text" placeholder="List key skills" required />
          <p class="helper">
            Key strengths in your role (e.g., API design, UX research, automation testing).
          </p>
        </div>

        <div class="form-group">
          <label>Technologies*</label>
          <input v-model="form.technologies" type="text" placeholder="List technologies" required />
          <p class="helper">
            Programming languages, frameworks, tools you use (e.g., React, Python, Figma).
          </p>
        </div>

        <div class="form-group">
          <label>Interests</label>
          <input v-model="form.interests" type="text" placeholder="List interests" />
          <p class="helper">
            Professional & personal interests (e.g., AI ethics, cooking, photography).
          </p>
        </div>

        <div class="language-form-group">
          <label>Language Skills*</label>
          <div class="language-grid-form">
            <div class="form-group">
              <input v-model="form.language1" type="text" placeholder="Language" />
            </div>
            <div class="form-group">
              <select v-model="form.fluency1" required>
                <option value="" disabled>Select level of fluency</option>
                <option v-for="level in fluencyLevels" :key="level" :value="level">
                  {{ level }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <input v-model="form.language2" type="text" placeholder="Language" />
            </div>
            <div class="form-group">
              <select v-model="form.fluency2">
                <option value="" disabled>Select level of fluency</option>
                <option v-for="level in fluencyLevels" :key="level" :value="level">
                  {{ level }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <input v-model="form.language3" type="text" placeholder="Language" />
            </div>
            <div class="form-group">
              <select v-model="form.fluency3">
                <option value="" disabled>Select level of fluency</option>
                <option v-for="level in fluencyLevels" :key="level" :value="level">
                  {{ level }}
                </option>
              </select>
            </div>
          </div>
          <p class="helper">
            List languages, denote fluency using
            <a
              href="https://en.wikipedia.org/wiki/Common_European_Framework_of_Reference_for_Languages"
              target="_blank"
              ref="noopener noreferrer"
              >the CEFR scale</a
            >
          </p>
        </div>

        <div class="level-form-group">
          <label>Salary Levels*</label>
          <div class="level-grid-form">
            <div class="form-group">
              <select v-model="form.expertiseLevel" required>
                <option value="" disabled>Select level of expertise</option>
                <option v-for="n in 6" :key="n" :value="n">{{ n }}</option>
              </select>
            </div>
            <div class="form-group">
              <select v-model="form.experienceLevel" required>
                <option value="" disabled>Select level of leadership</option>
                <option v-for="n in 6" :key="n" :value="n">{{ n }}</option>
              </select>
            </div>
          </div>
          <p class="helper">
            Self-assess according
            <a
              href="https://docs.google.com/document/d/1haO9oafK4SfjSrr2WRzFnPQed2l3XdUpNAJcqL0RxkI/edit?usp=sharing"
              target="_blank"
              ref="noopener noreferrer"
              >TeamStack’s salary system</a
            >
          </p>
        </div>

        <div class="form-group">
          <label>Availability*</label>
          <select v-model="form.availability" required>
            <option value="" disabled>Select availability</option>
            <option value="Immediate">Immediate</option>
            <option value="15 Days">15 Days</option>
            <option value="30 Days">30 Days</option>
            <option value="60 Days">60 Days</option>
            <option value="90 Days">90 Days</option>
          </select>
          <p class="helper">How soon can you start after receiving an offer?</p>
        </div>
      </div>

      <!-- Submit Button -->
      <div class="form-actions">
        <div v-if="submitStatus" :class="['status-message', submitStatus.type]">
          {{ submitStatus.message }}
        </div>
        <div class="action-container">
          <button type="submit" :disabled="isSubmitting" class="submit-button">
            {{ isSubmitting ? "Submitting..." : "Submit" }}
          </button>
        </div>
      </div>
    </BaseCard>
  </form>
</template>

<script setup>
import BaseCard from "@/components/common/BaseCard.vue";

import { computed, reactive, ref } from "vue";
import axios from "axios";
import { useReCaptcha } from "vue-recaptcha-v3";

const recaptcha = useReCaptcha();

const fluencyLevels = ["A1", "A2", "B1", "B2", "C1", "C2"];

const emit = defineEmits(["onSuccess"]);

const isSubmitting = ref(false);
const submitStatus = ref(null);

const form = reactive({
  firstName: "",
  lastName: "",
  email: "",
  linkedin: "",
  github: "",
  portfolio: "",
  basedInThailand: false,
  requiresWorkPermit: false,
  profileName: "",
  jobDomain: "",
  experience: "",
  sector: "",
  skills: "",
  technologies: "",
  interests: "",
  expertiseLevel: "",
  experienceLevel: "",
  availability: "",
  language1: "",
  fluency1: "",
  language2: "",
  fluency2: "",
  language3: "",
  fluency3: ""
});

const serverUrl = computed(() => {
  return process.env.NODE_ENV === "development"
    ? "http://localhost:3000"
    : "https://us-central1-teamstack-roster.cloudfunctions.net";
});

const submitForm = async () => {
  isSubmitting.value = true;
  submitStatus.value = null;

  // Get reCAPTCHA token
  await recaptcha.recaptchaLoaded();
  const token = await recaptcha.executeRecaptcha("submit");
  if (!token) {
    submitStatus.value = {
      type: "error",
      message: "Please complete the reCAPTCHA verification."
    };
    isSubmitting.value = false;
    return;
  }

  try {
    const response = await axios.post(`${serverUrl.value}/api/users`, {
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      linkedin: form.linkedin,
      github: form.github,
      portfolio: form.portfolio,
      basedInThailand: form.basedInThailand,
      requiresWorkPermit: form.requiresWorkPermit,
      profileName: form.profileName,
      jobDomain: form.jobDomain,
      experience: form.experience,
      skills: form.skills,
      sector: form.sector,
      technologies: form.technologies,
      interests: form.interests,
      availability: form.availability,
      expertiseLevel: form.expertiseLevel,
      experienceLevel: form.experienceLevel,
      language1: form.language1,
      language2: form.language2,
      language3: form.language3,
      language1Score: form.fluency1,
      language2Score: form.fluency2,
      language3Score: form.fluency3,
      recaptchaToken: token
    });

    if (response.data) {
      submitStatus.value = {
        type: "success",
        message: "Profile submitted successfully!"
      };
      resetForm();
    }
    emit("onSuccess");
  } catch (error) {
    console.error("Error submitting profile:", error);
    submitStatus.value = {
      type: "error",
      message: "Failed to submit profile. Please try again."
    };
  } finally {
    isSubmitting.value = false;
  }
};

const resetForm = () => {
  Object.keys(form).forEach((key) => {
    if (typeof form[key] === "boolean") {
      form[key] = false;
    } else {
      form[key] = "";
    }
  });
};
</script>

<style lang="scss" scoped>
.form-card-container-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 60px;
}

.form-card-container {
  padding: 80px 160px;
  background-color: #f9f6f2;
  border-color: #eee7dd;

  @media screen and (max-width: 768px) {
    padding: 32px 16px;
  }

  .header-title {
    margin-bottom: 40px;
  }

  .break-line {
    margin: 40px 0;
  }
}

.private-form-container {
  display: flex;
  flex-direction: column;
  gap: 40px;

  .private-grid-form {
    display: grid;
    grid-template-areas:
      "firstname lastname"
      "email email"
      "linkedin linkedin"
      "github github"
      "portfolio portfolio";
    column-gap: 20px;
    row-gap: 24px;

    @media screen and (max-width: 768px) {
      grid-template-areas:
        "firstname firstname"
        "lastname lastname"
        "email email"
        "linkedin linkedin"
        "github github"
        "portfolio portfolio";
    }

    .firstname-input {
      grid-area: firstname;
    }

    .lastname-input {
      grid-area: lastname;
    }

    .email-input {
      grid-area: email;
    }

    .linkedin-input {
      grid-area: linkedin;
    }

    .github-input {
      grid-area: github;
    }

    .portfolio-input {
      grid-area: portfolio;
    }
  }

  .work-permit-container {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 24px;
  }

  .why-we-ask-container,
  .what-this-means-container {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;

    .why-we-ask-title,
    .what-this-means-title {
      font-weight: 500;
      font-size: 24px;
      line-height: 32px;
      text-align: left;
    }

    .why-we-ask-message,
    .what-this-means-message,
    .what-this-means-message-content {
      font-weight: 400;
      font-size: 20px;
      line-height: 28px;
      text-align: left;
    }

    .what-this-means-message-content {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
    }
  }
}

.public-form-container {
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: repeat(9, auto);
  row-gap: 24px;

  .language-form-group,
  .level-form-group {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;

    .language-grid-form,
    .level-grid-form {
      width: 100%;
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-template-rows: 1fr;
      gap: 16px;

      @media screen and (max-width: 768px) {
        grid-template-columns: 1fr;
        grid-template-rows: 1fr 1fr;
      }
    }

    .helper {
      font-weight: 400;
      font-size: 14px;
      line-height: 22.4px;
      color: rgba(40, 40, 40, 0.8);
      text-align: left;
    }
  }
}

.form-actions {
  margin-top: 40px;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 16px;

  .action-container {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;

    @media screen and (max-width: 768px) {
      flex-direction: column;
      align-items: center;
    }
  }

  .g-recaptcha {
    margin-bottom: 16px;
  }
}
</style>
